import {$inject, $type} from "../utils/di-container.js";
import {DomDetector} from "../utils/dom-detector.js";
import {appContext} from "../badge2.context.js";
import { LoggerService } from './logger.service.js';
import { BadgeService } from './badge.service.js';
import { ResponsivenessService } from './responsiveness.service.js';
import { PlaceholderService } from './placeholder.service.js';
import { Api2Service } from './api2.service.js';
import { CssService } from "./css.service";
import { ScrapingService } from "./scraping.service";

/**
 * @typedef {'live'|'test'|'app'|'manager'} BadgeEnvironment
 * live: real badge displayed on a retailer site with real data
 * test: sandbox mode for usage on retailer test sites
 * app: badge is displayed for preview purposes inside the app
 * manager: badge is rendered using the universal badge manager tool
 */

/**
 * @typedef {object} _BadgeMeta
 * @property {string|number} productId
 * @property {string} brandName
 * @property {_BadgeEnvironment} environment
 * @property {_Language} language
 */

const invalidProductIdValues = [
  '',
  null,
  undefined,
  'null',
  'undefined',
  'EAN/GTIN',
  'Insert_EAN/GTIN_here',
  'Insert_EAN_GTIN_here',
  'unbekannt'
];

const invalidBrandNameValues = [
  '',
  null,
  undefined,
  'null',
  'undefined',
  'Brand_Name',
  'Insert_Brand_Name_here',
  'Insert_brand_name_here',
  'unbekannt'
];

/**
 * Service used to locate the badge2 metadata tag.
 */
export class MetadataService {

  /** @type {DomDetector} */
  _rootDomDetector;

  /** @type {LoggerService} */
  _logger;

  /** @type {DomDetectorQuery} */
  _metaQuery;

  /** @type {_BadgeMeta} */
  _badgeMeta;

  /** @type {_ApiResponse_fetchBadgeInfo} */
  _badgeInfo;

  [$inject](obtain) {
    this._rootDomDetector = obtain(DomDetector);
    this._logger = obtain(LoggerService);
    this._apiService = obtain(Api2Service);
    this._badgeService = obtain(BadgeService);
    this._placeholderService = obtain(PlaceholderService);
    this._responsivenessService = obtain(ResponsivenessService);
    this._cssService = obtain(CssService);
    this._scrapingService = obtain(ScrapingService);
  }

  _getBadgeMetaFromElement(element) {
    if (!element) {
      return {};
    }
    const badgeMeta = {
      productId: (element.getAttribute('data-authorizedby-product') || '').trim(),
      brandName: (element.getAttribute('data-authorizedby-brand') || '').trim(),
      environment: (element.getAttribute('data-authorizedby-environment') || 'live').trim(),
      language: (element.getAttribute('lang') || '').trim(),
    };

    if (invalidProductIdValues.includes(badgeMeta.productId)) {
      delete badgeMeta.productId;
    }
    if (invalidBrandNameValues.includes(badgeMeta.brandName)) {
      delete badgeMeta.brandName;
    }

    return badgeMeta;
  }

  /**
   * Starts looking for meta changes
   */
  start() {
    this._metaQuery = this._rootDomDetector.addQuery(['div[data-authorizedby]'], element => {
      if (element) {
        this._metaElement = element;

        if (this._badgeMeta) {
          this._logger.debug('metadata-tag/changed', 'Metadata tag changed.');
          return this._checkMetadataChanges();
        } else {
          if (this._detectorAttr) {
            this._detectorAttr.deactivate();
          }
          this._detectorAttr = new DomDetector({
            containerElement: this._metaElement,
            checkEvents: {
              structureChange: false,
              attributeChange: ['lang', 'data-authorizedby-product', 'data-authorizedby-brand', 'data-authorizedby-environment']
            },
          });

          this._detectorAttr.addCheck(() => {
            this._logger.debug('metadata-tag/changed', 'Metadata tag changed.');
            return this._checkMetadataChanges();
          });

          appContext.applyInject(this._detectorAttr);
          this._detectorAttr.activate(true);

          this._logger.debug('metadata-tag/added', 'Metadata tag added.');
          return this._checkMetadataChanges();
        }
      } else {
        if (this._metaElement) {
          delete this._metaElement;
          this._logger.debug('metadata-tag/removed', 'Metadata tag removed.');
          return this._checkMetadataChanges();
        }
      }
    });

    this._rootDomDetector.checkNow();
  }

  /**
   * When metadata is found, initialize badge service with the initial badge API call
   * Only continue if/after metadata is first discovered on this page
   */
  async _checkMetadataChanges(force = false) {
    const newMeta = this._getBadgeMetaFromElement(this._metaElement);
    const oldMeta = this._badgeMeta || {};

    if (newMeta.language === oldMeta.language
        && newMeta.productId === oldMeta.productId
        && newMeta.brandName === oldMeta.brandName
        && newMeta.environment === oldMeta.environment) {

      // unchanged
      if (!force) {
        this._logger.debug('metadata/unmodified', 'Metadata was not modified. No reload needed.', () => ({ oldMeta, newMeta }));
        return;
      }
    }

    if (this._metaElement) {
      this._badgeMeta = newMeta;
    } else {
      delete this._badgeMeta;
    }

    this._logger.info('metadata/modified', 'Metadata change detected. (Re-)Loading badges.\n' + JSON.stringify(this._badgeMeta, null, 2), () => ({ oldMeta, newMeta }));

    // stop looking for location changes until refresh is done
    this._badgeService.stopHandlingLocationChanges();

    // do not check placeholders until data is loaded
    this._placeholderService.stop();

    // de-activate all current badges first
    this._badgeService.deactivateAll('metadata-change');

    // (re-)load badge data from the b/e and (re-)initialize badges
    if (this._badgeMeta) {
      let outboundData;
      try {
        outboundData = this._scrapingService.scrapData();
      } catch (e) {
        // ignore any possible errors (avoid unforeseen badge render issues)
      }
      if (!this._badgeMeta?.productId && !outboundData?.productId && !this._badgeMeta?.brandName && !outboundData?.brandName) {
        // do not send outbound data if we don't have the id or the brand either as a param or within the data itself
        outboundData = null;
      }

      this._badgeInfo = await this._apiService.fetchBadgeInfo(this._badgeMeta, outboundData);
      this._badgeService.initialize(this._badgeMeta, this._badgeInfo, this._responsivenessService.platform);
      this._badgeService.startHandlingLocationChanges();
      this._cssService.applyCustomStyle();
      this._placeholderService.start();
    } else {
      this._logger.error('metadata/missing', 'Metadata missing. No badges will be shown.');
    }
  }

  /**
   * Stops looking for meta changes
   */
  stop() {
    this._rootDomDetector.removeQuery(this._metaQuery);
    if (this._detectorAttr) {
      this._detectorAttr.deactivate();
    }
  }

}

MetadataService[$type] = Symbol('MetadataService');
